import React, { useState, useEffect } from 'react';
import { useSearch } from '@tanstack/react-router';
import { FileText, User, FolderOpen, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Pagination, PaginationInfo } from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { SearchResult, SearchFilters, SearchService } from '../services/search-service';
import SearchResultItem from '../components/SearchResultItem';

export const SearchPage: React.FC = () => {
  const search = useSearch({ from: '/_authenticated/search' });
  const query = search.q as string || '';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<SearchFilters>({
    types: ['document', 'profile', 'collection']
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch search results
  useEffect(() => {
    const performSearch = async () => {
      if (query) {
        setIsLoading(true);
        try {
          const searchResponse = await SearchService.search(query, currentPage, 10, filters);
          setResults(searchResponse.results);
          setTotalResults(searchResponse.totalResults);
          setTotalPages(searchResponse.totalPages);
        } catch (error) {
          console.error('Search failed:', error);
          setResults([]);
          setTotalResults(0);
          setTotalPages(1);
        } finally {
          setIsLoading(false);
        }
      } else {
        setResults([]);
        setTotalResults(0);
        setTotalPages(1);
        setIsLoading(false);
      }
    };
    
    performSearch();
  }, [query, currentPage, filters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Use the router's navigate function instead of directly manipulating history
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleFilterChange = (type: 'document' | 'profile' | 'collection', checked: boolean) => {
    setFilters(prev => {
      const newTypes = checked
        ? [...(prev.types || []), type]
        : (prev.types || []).filter(t => t !== type);
      
      return {
        ...prev,
        types: newTypes.length > 0 ? newTypes : undefined
      };
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-bold tracking-tight">Search Results</h1>
        <form onSubmit={handleSearch} className="flex w-full max-w-lg items-center space-x-2">
          <Input
            type="search"
            placeholder="Search documents, profiles, collections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
          />
          <Button type="submit">Search</Button>
        </form>
      </div>

      {query && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {isLoading ? 'Searching...' : `${totalResults} results for "${query}"`}
            </span>
            {filters.types && filters.types.length < 3 && (
              <div className="flex gap-1">
                {filters.types.map(type => (
                  <Badge key={type} variant="secondary" className="capitalize">
                    {type}s
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="h-4 w-4" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Search Filters</SheetTitle>
                <SheetDescription>
                  Narrow down your search results by applying filters.
                </SheetDescription>
              </SheetHeader>
              <div className="py-4">
                <h3 className="mb-2 text-sm font-medium">Content Types</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="filter-documents" 
                      checked={filters.types?.includes('document')}
                      onCheckedChange={(checked) => handleFilterChange('document', checked === true)}
                    />
                    <label
                      htmlFor="filter-documents"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4 text-blue-500" />
                      Documents
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="filter-profiles" 
                      checked={filters.types?.includes('profile')}
                      onCheckedChange={(checked) => handleFilterChange('profile', checked === true)}
                    />
                    <label
                      htmlFor="filter-profiles"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                    >
                      <User className="h-4 w-4 text-green-500" />
                      Profiles
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="filter-collections" 
                      checked={filters.types?.includes('collection')}
                      onCheckedChange={(checked) => handleFilterChange('collection', checked === true)}
                    />
                    <label
                      htmlFor="filter-collections"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                    >
                      <FolderOpen className="h-4 w-4 text-amber-500" />
                      Collections
                    </label>
                  </div>
                </div>
                <Separator className="my-4" />
                {/* Additional filters can be added here */}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-md bg-muted animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 w-1/3 rounded-md bg-muted animate-pulse" />
                      <div className="h-4 w-1/4 rounded-md bg-muted animate-pulse" />
                      <div className="h-4 w-full rounded-md bg-muted animate-pulse" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : results.length > 0 ? (
        <>
          <div className="space-y-4">
            {results.map((result) => (
              <SearchResultItem key={`${result.type}-${result.id}`} result={result} />
            ))}
          </div>
          
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-8">
              <PaginationInfo
                currentPage={currentPage}
                pageSize={10}
                totalItems={totalResults}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showFirstLast={true}
                siblingCount={1}
                className="mt-0"
              />
            </div>
          )}
        </>
      ) : query ? (
        <Card>
          <CardHeader>
            <CardTitle>No results found</CardTitle>
            <CardDescription>
              We couldn't find any matches for "{query}". Try adjusting your search terms or filters.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <X className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No matching results</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">
                Try using different keywords, checking for typos, or removing filters to find what you're looking for.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Search the archive</CardTitle>
            <CardDescription>
              Enter a search term above to find documents, profiles, and collections.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex gap-4 mb-4">
                <FileText className="h-8 w-8 text-blue-500" />
                <User className="h-8 w-8 text-green-500" />
                <FolderOpen className="h-8 w-8 text-amber-500" />
              </div>
              <h3 className="text-lg font-medium">Start searching</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">
                Search for documents, profiles, and collections in the archive. Use filters to narrow down your results.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchPage;
